import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useLayoutEffect, type ReactNode } from 'react';

import { useSessionStore, type Impersonation, type Session } from '@/shared/api';
import { cacheScopeOf, forgetCachedResponses } from '@/shared/api/client';

import { forgetPersistedQueries } from './query-client';

interface SessionView {
    session: Session | null;
    impersonation: Impersonation | null;
}

/**
 * Wie kijkt er, bij welk bedrijf, en als wie. Verandert een van die drie, dan is
 * alles wat er in beeld en in de cache staat van een ander.
 *
 * Een nieuw token voor dezelfde persoon in hetzelfde bedrijf (verlengen, of de
 * sessie bijwerken met `GET /auth/me`) verandert de sleutel niet.
 */
function sessionViewKey({ session, impersonation }: SessionView): string {
    return [
        session?.user.uuid ?? '-',
        session?.tenant?.uuid ?? '-',
        impersonation
            ? `meekijken:${impersonation.tenantId}:${impersonation.userName}:${impersonation.expiresAt ?? ''}`
            : '-',
    ].join('|');
}

/** Wist bewaarde gegevens die niet bij deze sessie horen. */
function forgetOtherCaches(view: SessionView): void {
    const scope = cacheScopeOf(view);

    void forgetCachedResponses(scope).catch(() => undefined);
    void forgetPersistedQueries(scope).catch(() => undefined);
}

/**
 * Houdt de querycache bij de sessie.
 *
 * Zodra de persoon, het bedrijf of het meekijken verandert - inloggen,
 * uitloggen, verlopen, wisselen van omgeving, meekijken starten of stoppen -
 * gaat alles eruit: de querycache in het geheugen, de bewaarde querycache en de
 * bewaarde GET-antwoorden die niet bij de nieuwe sessie horen.
 *
 * Dat gebeurt **synchroon op de wijziging in de sessiestore**, niet in een effect
 * na de render: het eerste scherm dat met de nieuwe sessie rendert, vindt een
 * lege cache. `SessionScope` zorgt ervoor dat er ook geen component met oude
 * gegevens blijft staan.
 *
 * Bij het opstarten ruimt hij op wat er nog van een vorige sessie lag, voor het
 * geval de app werd afgesloten voordat dat lukte.
 */
export function SessionCacheGuard({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();

    useLayoutEffect(() => {
        forgetOtherCaches(useSessionStore.getState());

        return useSessionStore.subscribe((state, previous) => {
            if (sessionViewKey(state) === sessionViewKey(previous)) {
                return;
            }

            void queryClient.cancelQueries().catch(() => undefined);
            queryClient.clear();
            forgetOtherCaches(state);
        });
    }, [queryClient]);

    return <>{children}</>;
}

/**
 * Bouwt zijn inhoud opnieuw op zodra persoon, bedrijf of meekijken verandert.
 *
 * Een scherm dat alleen query-gegevens toont, rendert niet vanzelf opnieuw als
 * de sessie wisselt. Zonder deze grens zou het nog één frame - of tot de
 * volgende update - de gegevens van de vorige sessie laten zien. Met de sleutel
 * verdwijnt de oude boom in dezelfde render waarin de nieuwe sessie verschijnt.
 *
 * Staat om de routes heen, niet om de providers: de sync-engine regelt zijn
 * eigen wissel en hoort niet opnieuw te starten.
 */
export function SessionScope({ children }: { children: ReactNode }) {
    const key = useSessionStore(sessionViewKey);

    return <Fragment key={key}>{children}</Fragment>;
}
